import React, {useCallback, useMemo, useRef, useState} from "react";
import classnames from "classnames";
import {ReactComponent as CompareOutLinedIcon} from "../../../../assets/images/compare-outlined.svg";
import ProductTagsContainer from "./product-tags-container";
import {formatMoney} from "../../../../core/services/utils/utils";
import {Box, Button, Card, CircularProgress, Fade, SvgIcon, Tooltip, Typography} from "@mui/material";
import Slider from "react-slick";
import LazyImage from "../lazy-image";
import {AutorenewRounded} from "@mui/icons-material";
import './index.scss';
import {Link} from "react-router-dom";
import useAccessLevels from "../../../hooks/use-access-levels";
import {productInformationPopoverTabs} from "../draggable-popover/product-information-popover/product-information";
import {ProductTitleText} from "../../../views/panel/operator/products/single";
import DeliveryModeChip from "../delivery-mode-chip";


const LookupProductCard = ({
                               info,
                               priceInfo,
                               availabilityInfo,
                               link,
                               canGetExtraInfo,
                               additionalProperties,
                               readonly,
                               showTooltip = readonly,
                               comparing,
                               inCompareList,
                               gettingExtraInfo,
                               toggleInCompareList,
                               onGetExtraInfoClicked,
                               onProductClicked,
                               onVarianceClicked,
                               selectedVariety,
                               cardExtras,
                           }) => {
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const sliderRef = useRef(null);
    const operatorAccessLevels = useAccessLevels();

    const images = useMemo(() => {
        let result = !info?.images?.length ? [null] : info?.images;
        result = result.filter(e => !!e && !e.endsWith('part-not-av.jpg'));
        return !result.length ? [null] : result;
    }, [info?.images])
    const codeList = useMemo(() => [...new Set((info?.codes) ?? [])], [info?.codes])
    const priceNotAvailable = useMemo(() => priceInfo?.newPrice <= 0, [priceInfo?.newPrice]);
    const hasNewPrice = useMemo(() => priceInfo?.newPrice < priceInfo?.originalPrice, [priceInfo?.newPrice, priceInfo?.originalPrice]);

    const availability = useMemo(() => availabilityInfo?.filter(e => e._has) ?? [], [availabilityInfo]);
    const availabilityColorCode = useMemo(() => availability?.length === 1 ? availability[0].availabilityColorCode : null, [availability]);

    /**
     * After each change in the slide, sets the [activeSlide] to the received number
     * @param index
     */
    const afterSlideChange = useCallback((index) => setActiveImageIndex(index), []);

    return (
        <>
            <Tooltip title={readonly && showTooltip ? 'This search is from a search log. If you want to search again, please click on the "Exit Log" button at the top.' : ''} followCursor>
                <Box component={'span'} sx={{width: '100%', height: '100%', display: 'inline-block', position: 'relative', cursor: readonly ? 'not-allowed' : 'pointer'}}>
                    <Box
                        onDragStart={e => e.preventDefault()}
                        onClick={(e) => onProductClicked?.(e, info, link)}
                        {...((!readonly && !!link.href) && {
                            component: Link,
                            to: link.href,
                            replace: link.replace,
                        })}
                    >
                        <div
                            className={classnames('lookup-product-card')}
                            style={{
                                ...(availabilityColorCode ? {
                                    borderBottomColor: availabilityColorCode,
                                    borderBottomWidth: 3,
                                    borderBottomLeftRadius: 0,
                                    borderBottomRightRadius: 0,
                                } : {})
                            }}
                        >
                            <div className={'product-header'}>
                                <Box className={'product-header-line w-100'}>
                                    <ProductTitleText
                                        title={info?.title ?? ""}
                                        partNo={info?.partNo}
                                        partNoPopoverProps={{
                                            partNoClassName: 'part-no',
                                            startingTab: productInformationPopoverTabs.technicalInfo,
                                            wrapperProps: {component: 'p'},
                                            buttonProps: {className: 'p-0'},
                                            iconProps: {color: 'primary'},
                                        }}
                                        PartNoWrapper={({children}) => (
                                            <span onClick={e => e.preventDefault() || e.stopPropagation()}>
                                                {children}
                                            </span>
                                        )}
                                    />
                                    <Box sx={{mt: 0.5, mb: 0.5}}>
                                        <DeliveryModeChip deliveryMode={info?.deliveryMode} size={'small'}/>
                                    </Box>
                                    <p className={'category'}>
                                        {[info?.rootCategoryName ?? "", info?.leafCategoryName ?? ""].filter(Boolean).join(' > ')}
                                    </p>
                                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                                        {
                                            !!info?.variety && !!onVarianceClicked && operatorAccessLevels.packages.lookup.productVariants &&
                                            <Box className={'product-header-line w-100 d-flex justify-content-end'}>
                                                <Button
                                                    variant={'text'}
                                                    color={'secondary'}
                                                    onClick={e => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        onVarianceClicked(info);
                                                    }}
                                                >
                                                    {selectedVariety === info.variety ? "" : "More Like This"}
                                                </Button>
                                            </Box>
                                        }
                                        {
                                            operatorAccessLevels.packages.sales.pricesInLookup &&
                                            priceInfo &&
                                            <div className={'product-header-line'}>
                                                {
                                                    hasNewPrice &&
                                                    <Fade in={hasNewPrice}>
                                                        <p className={'price new'}>
                                                            {formatMoney(priceInfo?.newPrice)}
                                                        </p>
                                                    </Fade>
                                                }
                                                {
                                                    priceNotAvailable
                                                        ? <p className={'price not-available'}>
                                                            {/*Price Not Available*/}
                                                        </p>
                                                        : <p className={classnames('price', {'old': hasNewPrice})}>
                                                            {formatMoney(priceInfo?.originalPrice)}
                                                        </p>
                                                }
                                            </div>
                                        }
                                    </Box>
                                </Box>
                            </div>
                            <div className={'product-body'}>
                                <div className={'product-images'}>
                                    <Slider
                                        autoplay={false}
                                        infinite={true}
                                        initialSlide={0}
                                        afterChange={afterSlideChange}
                                        slidesToShow={1}
                                        ref={sliderRef}
                                    >
                                        {
                                            images.map((image) => (
                                                <div key={image ?? 'placeholder'} className="img">
                                                    <LazyImage
                                                        src={image}
                                                        alt={image}
                                                        height={165}
                                                        onContextMenu={(e) => e.preventDefault()}
                                                        onDragStart={(e) => e.preventDefault()}
                                                        className={'disable-downloading-image'}
                                                    />
                                                </div>
                                            ))
                                        }
                                    </Slider>
                                    {
                                        images.length > 1 &&
                                        <div className={'custom-dots'}>
                                            {
                                                images.map((item, index) => (
                                                    <div
                                                        key={item ?? 'placeholder'}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            sliderRef.current?.slickGoTo(index);
                                                        }}
                                                        className={classnames(
                                                            'dot',
                                                            {
                                                                'active': index === activeImageIndex,
                                                                'active-minus-one': (index === activeImageIndex + 1) || (index === activeImageIndex - 1),
                                                                'active-minus-two': (index === activeImageIndex + 2) || (index === activeImageIndex - 2),
                                                            }
                                                        )}/>
                                                ))
                                            }
                                        </div>
                                    }
                                </div>
                                {
                                    (!!availability?.length) &&
                                    <Box sx={{display: 'flex', flexDirection: 'column', width: '100%'}}>
                                        <Typography variant={'body2'} fontWeight={600} color={'text.primary'} width={'100%'} textAlign={"start"}>
                                            Availability:
                                        </Typography>
                                        <div className={'d-flex flex-wrap w-100 mt-2'} style={{gap: 8}}>
                                            {
                                                availability.map((availability) => (
                                                    <Tooltip
                                                        key={availability.warehouse}
                                                        title={`Availability of ${availability.fullName}${availability.partner ? ` from ${availability.partner}` : ""}: ${availability.availability}`}
                                                        arrow
                                                    >
                                                        <Card
                                                            variant={'elevation'}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 0.5,
                                                                boxShadow: 'none',
                                                                backgroundColor: 'secondary.light',
                                                                borderRadius: 1,
                                                                paddingInline: 1,
                                                                paddingBlock: 0.5,
                                                            }}
                                                        >
                                                            <Box sx={{
                                                                width: 10,
                                                                height: 10,
                                                                borderRadius: '100%',
                                                                backgroundColor: availability.availabilityColorCode,
                                                            }}/>
                                                            <Typography variant={'caption'} fontWeight={600}>
                                                                {availability.warehouse}
                                                                <Typography
                                                                    component={'span'}
                                                                    sx={{display: 'inline-block', marginInlineStart: 0.5}}
                                                                    variant={'body2'}
                                                                    fontWeight={600}
                                                                    color={'secondary'}
                                                                >
                                                                    ({availability.availability})
                                                                </Typography>
                                                            </Typography>
                                                        </Card>
                                                    </Tooltip>
                                                ))
                                            }
                                        </div>
                                    </Box>
                                }
                                {
                                    (!!additionalProperties?.length) &&
                                    <Box sx={{display: 'flex', flexDirection: 'column', width: '100%'}}>
                                        <Typography variant={'body2'} fontWeight={600} color={'text.primary'} width={'100%'} textAlign={'start'}>
                                            Properties:
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                flexWrap: 'wrap',
                                                alignContent: 'flex-start',
                                                gap: 1,
                                                width: '100%',
                                                mt: 1,
                                                // ~2 property chip rows; overflow scrolls sideways
                                                maxHeight: 80,
                                                pb: 1.5,
                                                overflowX: 'auto',
                                                overflowY: 'hidden',
                                                scrollbarWidth: 'thin',
                                                boxSizing: 'border-box',
                                            }}
                                        >
                                            {
                                                additionalProperties?.map((property) => (
                                                    <Tooltip
                                                        key={`${property.title}-${property.value}`}
                                                        title={`${property.title}: ${property.tooltipValue ?? property.displayValue}`}
                                                        arrow
                                                    >
                                                        <Card
                                                            variant={'elevation'}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 0.5,
                                                                flexShrink: 0,
                                                                boxShadow: 'none',
                                                                backgroundColor: 'secondary.light',
                                                                borderRadius: 1,
                                                                paddingInline: 1,
                                                                paddingBlock: 0.5,
                                                            }}
                                                        >
                                                            <Typography variant={'caption'} fontWeight={600}>
                                                                {property.title}:
                                                                <Typography
                                                                    component={'span'}
                                                                    sx={{display: 'inline-block', marginInlineStart: 0.5}}
                                                                    variant={'body2'}
                                                                    fontWeight={600}
                                                                    color={'secondary'}
                                                                >
                                                                    {property.displayValue}
                                                                </Typography>
                                                            </Typography>
                                                        </Card>
                                                    </Tooltip>
                                                ))
                                            }
                                        </Box>
                                    </Box>
                                }
                                {
                                    !!info?.tags?.length &&
                                    <ProductTagsContainer
                                        tags={info?.tags}
                                    />
                                }
                                {
                                    codeList.length > 0 &&
                                    <div className={'product-codes mt-2'}>
                                        {
                                            codeList.map(code => (
                                                <p key={code}>
                                                    {code ?? ''}
                                                </p>
                                            ))
                                        }
                                    </div>
                                }
                                {
                                    !!cardExtras &&
                                    <Box
                                        sx={{width: '100%', mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider'}}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                    >
                                        {cardExtras}
                                    </Box>
                                }
                            </div>
                            {
                                operatorAccessLevels.packages.lookup.comparisonList && toggleInCompareList &&
                                <button
                                    onClick={(e) => toggleInCompareList(e, info)}
                                    disabled={comparing?.[info?.id]}
                                    className={classnames(
                                        'button animating-icon-button compare',
                                        {'active': inCompareList?.[info?.id], 'selected': inCompareList?.[info?.id]}
                                    )}
                                >
                                    <Tooltip
                                        placement={'top-end'}
                                        title={inCompareList?.[info?.id] ? 'Remove from compare list' : 'Add to compare list'}
                                    >
                                        {
                                            comparing?.[info?.id]
                                                ? <CircularProgress size={20}/>
                                                : <SvgIcon component={CompareOutLinedIcon} className={'color'}/>
                                        }
                                    </Tooltip>
                                </button>
                            }
                            {
                                !!canGetExtraInfo && onGetExtraInfoClicked &&
                                <button
                                    onClick={(e) => onGetExtraInfoClicked(e, info)}
                                    disabled={gettingExtraInfo?.[info?.id]}
                                    className={classnames('button animating-icon-button refresh',)}
                                >
                                    <Tooltip
                                        placement={'top-end'}
                                        title={"Fetch Availability"}
                                    >
                                        {
                                            gettingExtraInfo?.[info?.id]
                                                ? <CircularProgress size={20}/>
                                                : <AutorenewRounded className={'color'}/>
                                        }
                                    </Tooltip>
                                </button>
                            }
                        </div>
                    </Box>
                </Box>
            </Tooltip>
        </>
    );
}

export default LookupProductCard;
